/* ============================================================
   gimbal.cloud — typed mock data
   Ported from gimbal_landing/design_handoff_gimbal_cloud_landing/data.js
   ============================================================ */

export interface SpaceData {
  id: string;
  name: string;
  img: string;
  desc: string;
  region: string;
  ue: string;
  actors: number;
  agents: number;
  intents_24h: number;
  status: string;
  tag: 'cyan' | 'mag';
  feature?: boolean;
  wide?: boolean;
  class_hint?: string;
}

export interface MosaicTileSpec {
  size: string;
  tag: 'cyan' | 'mag';
}

export interface VocabFunction {
  name: string;
  weight: number;
}

export interface VocabData {
  agents: string[];
  functions: VocabFunction[];
  actorsBySpace: Record<string, string[]>;
}

export const SPACES: SpaceData[] = [
  {
    id: 'sp_wh_047', name: 'Warehouse Sim · Bay 12',
    img: '/assets/spaces/warehouse.jpg',
    desc: 'Autonomous forklift fleet, 47 shelving units, agent-directed pick & pack over an 8-hour shift. Runs on a nightly benchmark schedule.',
    region: 'us-west-2', ue: '5.4.2', actors: 47, agents: 6, intents_24h: 8213,
    status: 'active', tag: 'cyan', feature: true, class_hint: 'logistics',
  },
  {
    id: 'sp_ct_022', name: 'Neo-Kowloon Nights',
    img: '/assets/spaces/city.jpg',
    desc: 'Open-world neon city, 32 wanderer NPCs each on their own LLM. Public demo Space.',
    region: 'ap-northeast-1', ue: '5.4.4', actors: 128, agents: 32, intents_24h: 21744,
    status: 'active', tag: 'mag', wide: true,
  },
  {
    id: 'sp_av_009', name: 'AV Lab · Test Track 3',
    img: '/assets/spaces/av-lab.jpg',
    desc: 'Sensor-in-the-loop test rig for autonomous prototype v7.',
    region: 'us-west-2', ue: '5.3.2', actors: 18, agents: 2, intents_24h: 942,
    status: 'active', tag: 'cyan',
  },
  {
    id: 'sp_tv_101', name: 'Cloakwood Tavern',
    img: '/assets/spaces/tavern.jpg',
    desc: 'Living NPC tavern from an in-development RPG. Every patron is claimable.',
    region: 'eu-central-1', ue: '5.4.2', actors: 24, agents: 11, intents_24h: 4482,
    status: 'active', tag: 'mag',
  },
  {
    id: 'sp_fc_014', name: 'Assembly Line · Kraft Motors',
    img: '/assets/spaces/factory.jpg',
    desc: 'Digital twin of a Detroit assembly plant. Agents run scenario batches nightly.',
    region: 'us-west-2', ue: '5.4.2', actors: 62, agents: 4, intents_24h: 12082,
    status: 'active', tag: 'cyan', wide: true,
  },
  {
    id: 'sp_pt_003', name: 'Port Terminal · LA-Long Beach',
    img: '/assets/spaces/port.jpg',
    desc: 'Container throughput sim with 8 autonomous crane agents.',
    region: 'us-west-2', ue: '5.4.2', actors: 89, agents: 8, intents_24h: 6702,
    status: 'active', tag: 'cyan',
  },
  {
    id: 'sp_sc_055', name: 'Kepler Station · Deck 7',
    img: '/assets/spaces/spacecraft.jpg',
    desc: 'Sci-fi bridge scenario. Turn-based command drills with LLM XOs.',
    region: 'eu-central-1', ue: '5.4.4', actors: 12, agents: 3, intents_24h: 1204,
    status: 'active', tag: 'mag',
  },
  {
    id: 'sp_nr_071', name: 'Sunset Blvd · 1974',
    img: '/assets/spaces/noir.jpg',
    desc: 'AI-directed noir short film in production. Camera, lights and 4 characters agent-controlled.',
    region: 'us-west-2', ue: '5.4.2', actors: 14, agents: 5, intents_24h: 3117,
    status: 'active', tag: 'mag',
  },
];

export const MOSAIC_SPECS: MosaicTileSpec[] = [
  { size: 's-1', tag: 'cyan' },
  { size: 's-2', tag: 'mag' },
  { size: 's-3', tag: 'cyan' },
  { size: 's-4', tag: 'mag' },
  { size: 's-5', tag: 'cyan' },
];

export const VOCAB: VocabData = {
  agents: [
    'cld-x92a', 'gpt5-r7', 'cld-op4b', 'opus-ml3', 'codex-a1',
    'sonnet-v9', 'opus-c2', 'claw-9x', 'gpt-o5', 'cld-ent7',
    'grok-4k', 'cld-lab2', 'gemini-3f', 'cld-e8',
  ],
  functions: [
    { name: 'move_actor', weight: 6 },
    { name: 'set_actor_property', weight: 4 },
    { name: 'call_actor_function', weight: 3 },
    { name: 'claim_actor', weight: 2 },
    { name: 'spawn_actor', weight: 2 },
    { name: 'get_actor_transform', weight: 3 },
    { name: 'get_viewport_screenshot', weight: 1 },
    { name: 'capture_actor_view', weight: 1 },
    { name: 'destroy_actor', weight: 1 },
    { name: 'release_actor', weight: 1 },
  ],
  actorsBySpace: {
    'sp_wh_047': ['BP_Forklift_01', 'BP_Forklift_02', 'BP_Worker_A', 'BP_Worker_B', 'BP_Shelf_12', 'BP_ConveyorBelt', 'BP_PalletJack_03'],
    'sp_ct_022': ['BP_Wanderer_007', 'BP_Wanderer_012', 'BP_HoloSign_44', 'BP_StreetLamp_88', 'BP_Vendor_02', 'BP_Car_Neon_31'],
    'sp_av_009': ['BP_Prototype_V7', 'BP_TrafficCone_11', 'BP_LIDAR_Array', 'BP_TestPedestrian_A'],
    'sp_tv_101': ['BP_Barkeep', 'BP_Patron_Bard', 'BP_Patron_Rogue', 'BP_Fireplace', 'BP_Lantern_03'],
    'sp_fc_014': ['BP_Robot_Weld_01', 'BP_Robot_Weld_02', 'BP_Chassis_Line', 'BP_ConveyorMain', 'BP_Crane_Overhead'],
    'sp_pt_003': ['BP_Crane_Gantry_04', 'BP_Container_Truck_09', 'BP_Container_Red_144', 'BP_Ship_Panamax'],
    'sp_sc_055': ['BP_XO_Chen', 'BP_Nav_Officer', 'BP_Console_Main', 'BP_Viewport_Star'],
    'sp_nr_071': ['BP_Detective_Kane', 'BP_CamActor_Main', 'BP_KeyLight', 'BP_TaxiCab', 'BP_Bystander_A'],
  },
};

// ── Helpers (ported from app.js) ──

export function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

export function pickWeighted(arr: VocabFunction[]): string {
  const total = arr.reduce((s, x) => s + x.weight, 0);
  let r = Math.random() * total;
  for (const x of arr) { r -= x.weight; if (r <= 0) return x.name; }
  return arr[0]!.name;
}

export function randInt(a: number, b: number): number {
  return Math.floor(Math.random() * (b - a + 1)) + a;
}

export function pad(n: number, w: number = 2): string {
  return String(n).padStart(w, '0');
}

export function fmtTs(d: Date = new Date()): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

export function fmtRel(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  return `${Math.floor(secs / 3600)}h`;
}
